import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';

const SoftwareCTA = () => {
  const [service, setService] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();

  const handleServiceChange = (e) => {
    const newService = e.target.value;
    setService(newService);
    setIsFormValid(newService !== '');
  };

  const handleSubmit = () => {
    if (isFormValid) {
      // For the consultation service, redirect to PayPal
      if (service === 'consultation') {
        // Use window.open instead of window.location.href to open in a new tab
        window.open('https://www.paypal.com/ncp/payment/4NCSYB2WGQDRU', '_blank');
      } else {
        // For other services, redirect to the business consultation page
        navigate('/business-consultation', { state: { service } });
      }
    }
  };

  return (
    <CTASection id="contact">
      <CTABackground>
        <OverlayGradient />
        <CTAContentWrapper>
          <CTAHeader>
            <LimitedOfferTag>LIMITED TIME OFFER</LimitedOfferTag>
            <CTATitle>Transform Your Business With AI</CTATitle>
            <CTASubtitle>
              Join the businesses that are leveraging custom AI solutions to automate processes, gain insights, and outperform competitors
            </CTASubtitle>
          </CTAHeader>
          
          <CTABoxes>
            <ActionBox>
              <ActionTitle>Book Your AI Consultation</ActionTitle>
              <ActionSubtitle>Expert guidance to help you implement AI in your business - select your service below</ActionSubtitle>
              
              <CTAForm>
                <SelectGroup>
                  <Select 
                    value={service}
                    onChange={handleServiceChange}
                  >
                    <option value="" disabled>What are you interested in?</option>
                    <option value="consultation">AI Consultation ($199)</option>
                    <option value="integration">Custom AI Integration</option>
                    <option value="development">Full AI Solution Development</option>
                    <option value="other">Other/Not Sure Yet</option>
                  </Select>
                </SelectGroup>
                <ActionButton 
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  isValid={isFormValid}
                >
                  {service === 'consultation' ? 'PROCEED TO PAYMENT' : 'REQUEST INFORMATION'}
                </ActionButton>
              </CTAForm>
              
              <SecurityNotice>
                <LockIcon>🔒</LockIcon>
                <SecurityText>Secure payment processing via PayPal</SecurityText>
              </SecurityNotice>
              
              {service === 'consultation' && (
                <ConsultationInfo>
                  <InfoIcon>ℹ️</InfoIcon>
                  <InfoText>After payment, you'll be able to schedule your consultation call</InfoText>
                </ConsultationInfo>
              )}
            </ActionBox>
            
            <GuaranteeBox>
              <GuaranteeTitle>Why Choose Our Team</GuaranteeTitle>
              
              <GuaranteeItems>
                <GuaranteeItem>
                  <GuaranteeIcon>✓</GuaranteeIcon>
                  <GuaranteeContent>
                    <GuaranteeItemTitle>Senior US-Based Engineers</GuaranteeItemTitle>
                    <GuaranteeItemText>Every project is built by engineers with 7+ years of experience.</GuaranteeItemText>
                  </GuaranteeContent>
                </GuaranteeItem>
                
                <GuaranteeItem>
                  <GuaranteeIcon>✓</GuaranteeIcon>
                  <GuaranteeContent>
                    <GuaranteeItemTitle>Accelerated Delivery</GuaranteeItemTitle>
                    <GuaranteeItemText>We complete projects in a fraction of the typical timeline.</GuaranteeItemText>
                  </GuaranteeContent>
                </GuaranteeItem>
                
                <GuaranteeItem>
                  <GuaranteeIcon>✓</GuaranteeIcon>
                  <GuaranteeContent>
                    <GuaranteeItemTitle>100% Money-Back Guarantee</GuaranteeItemTitle>
                    <GuaranteeItemText>Not satisfied with our delivery? Get a complete refund.</GuaranteeItemText>
                  </GuaranteeContent>
                </GuaranteeItem>
              </GuaranteeItems>
              
              <TestimonialQuote>
                "Their AI consultation saved us from making a $50,000 mistake with the wrong approach. Their guidance and implementation has paid for itself many times over."
                <QuoteAuthor>— David Miller, CTO of TechSolutions</QuoteAuthor>
              </TestimonialQuote>
            </GuaranteeBox>
          </CTABoxes>
          
          <FinalCTA>
            <FinalCTAText>Special prices for AI development end in:</FinalCTAText>
            <CountdownDisplay>11:59:47</CountdownDisplay>
            <ScarcityText>Only 3 consultation slots remaining this week</ScarcityText>
          </FinalCTA>
        </CTAContentWrapper>
      </CTABackground>
    </CTASection>
  );
};

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(255, 71, 87, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CTASection = styled.section`
  position: relative;
  padding: 120px 0;
  overflow: hidden;
  
  @media (max-width: 991px) {
    padding: 80px 0;
  }
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const CTABackground = styled.div`
  position: relative;
  background: url('/images/cta-bg.jpg') center center/cover no-repeat;
  background-color: #024A5F;
  height: 100%;
  width: 100%;
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(2, 74, 95, 0.95) 0%, rgba(8, 113, 164, 0.9) 100%);
  z-index: 1;
`;

const CTAContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const CTAHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const LimitedOfferTag = styled.div`
  display: inline-block;
  background-color: #FF4757;
  color: white;
  font-weight: 700;
  padding: 10px 25px;
  border-radius: 30px;
  font-size: 1rem;
  margin-bottom: 25px;
  animation: ${pulse} 2s infinite;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 8px 20px;
    margin-bottom: 20px;
  }
`;

const CTATitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 991px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CTABoxes = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 991px) {
    flex-direction: column;
  }
`;

const ActionBox = styled.div`
  flex: 1;
  background: white;
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ActionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const ActionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
`;

const SelectGroup = styled.div`
  width: 100%;
`;

const Select = styled.select`
  width: 100%;
  padding: 15px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 15px;
  
  &:focus {
    border-color: #024A5F;
    box-shadow: 0 0 0 2px rgba(2, 74, 95, 0.2);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${props => props.isValid ? 'linear-gradient(135deg, #FF4757, #FF7B69)' : '#A0A0A0'};
  color: white;
  font-size: 1rem;
  font-weight: 700;
  padding: 16px;
  border: none;
  border-radius: 8px;
  cursor: ${props => props.isValid ? 'pointer' : 'not-allowed'};
  transition: all 0.3s ease;
  text-transform: uppercase;
  box-shadow: ${props => props.isValid ? '0 8px 15px rgba(255, 71, 87, 0.3)' : 'none'};
  
  &:hover {
    transform: ${props => props.isValid ? 'translateY(-3px)' : 'none'};
    box-shadow: ${props => props.isValid ? '0 12px 20px rgba(255, 71, 87, 0.4)' : 'none'};
  }
  
  &:active {
    transform: ${props => props.isValid ? 'translateY(0)' : 'none'};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 14px;
  }
`;

const SecurityNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
`;

const LockIcon = styled.span`
  margin-right: 8px;
  font-size: 0.9rem;
`;

const SecurityText = styled.span`
  font-size: 0.8rem;
  color: #888;
`;

const GuaranteeBox = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  animation: ${float} 6s ease-in-out infinite, ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const GuaranteeTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 25px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 20px;
  }
`;

const GuaranteeItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 25px;
`;

const GuaranteeItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const GuaranteeIcon = styled.div`
  width: 24px;
  height: 24px;
  background-color: #024A5F;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 0.8rem;
  flex-shrink: 0;
`;

const GuaranteeContent = styled.div`
  flex: 1;
`;

const GuaranteeItemTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const GuaranteeItemText = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const TestimonialQuote = styled.div`
  font-style: italic;
  color: #555;
  font-size: 1.05rem;
  line-height: 1.6;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #eaeaea;
`;

const QuoteAuthor = styled.div`
  font-style: normal;
  font-weight: 600;
  margin-top: 10px;
  font-size: 0.95rem;
  color: #333;
`;

const FinalCTA = styled.div`
  text-align: center;
  animation: ${fadeIn} 1.2s ease-out;
`;

const FinalCTAText = styled.p`
  font-size: 1.3rem;
  color: white;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CountdownDisplay = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ScarcityText = styled.p`
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 8px 15px;
  }
`;

const ConsultationInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 12px 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #024A5F;
`;

const InfoIcon = styled.span`
  margin-right: 10px;
  font-size: 1rem;
`;

const InfoText = styled.span`
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
`;

export default SoftwareCTA; 